/* eslint-disable import/max-dependencies */
import React from "react";
import { withHookPropsState } from "@fas/cpa-cabinet-ui";
import type { Column } from "@fas/cpa-cabinet-ui/lib/Table/Table.types";
import dayjs from "dayjs";
import DateRangeDateCellDefault from "@fas/cpa-cabinet-ui/lib/DateRangePicker/DateRangeDateCell";
import DateRangePresetsDefault from "@fas/cpa-cabinet-ui/lib/DateRangePicker/DateRangePresets";
import { withStyles } from "@mui/styles";
import DialogComponent from "@fas/cpa-cabinet-ui/lib/DateRangePicker/DateRangeDialog";
import DateRangeFooter from "../DateRangeFooter";
import { maxDate, minDate, presets } from "./presets";
import SelectBody from "../SelectPopup/SelectBody";
import { useDictionaryValueList } from "../../hooks/useDictionaryValueList";
import { SelectPopupField } from "../SelectPopup/SelectPopup";
import TextFieldMultiselectBody from "../TextFieldMultiselect/TextFieldMultiselectBody";
import { useMemoryOptions } from "../../hooks/useMemoryOptions";

const StyledTextFieldMultiselectBody = withHookPropsState(
  withStyles({
    popper: {
      maxWidth: "360px",
    },
  })(TextFieldMultiselectBody),
  useMemoryOptions
);

const DateRangePicker = withStyles({})(DialogComponent);
const CountrySelect = withHookPropsState(SelectBody, useDictionaryValueList);
const Select = SelectPopupField;
const formControlSx = {
  "& > .MuiFormLabel-root > .MuiTypography-root": {
    fontWeight: 700,
    lineHeight: "30px",
    fontSize: "18px",
  },
};
// eslint-disable-next-line import/prefer-default-export
export const transactionReportColumnsMap: Record<string, Column<any>> = {
  date: {
    field: "date",
    label: "Date",
    searchable: true,
    sortable: true,
    SearchComponent: ({ onClose, onChange, value }: any): any => {
      const changeDates: any = ({
        startDate: start,
        endDate: end,
      }: {
        startDate: any;
        endDate: any;
      }) => {
        onChange({
          date: {
            from: dayjs(start).format("YYYY-MM-DD"),
            to: dayjs(end).format("YYYY-MM-DD"),
          },
        });
        onClose();
      };

      return (
        <DateRangePicker
          applyOnClose
          applyOnSelect
          startDate={value.from}
          endDate={value.to}
          onChange={changeDates}
          onChangePreset={changeDates}
          onClose={onClose}
          currentPreset=""
          presets={presets}
          minDate={minDate}
          maxDate={maxDate}
          CellComponent={DateRangeDateCellDefault}
          PresetsComponent={DateRangePresetsDefault}
          FooterComponent={DateRangeFooter}
        />
      );
    },
  },
  conversion_time: {
    label: "Conversion time",
    field: "conversion_time",
  },
  commission: {
    label: "Commission $",
    field: "commission",
  },
  country: {
    field: "country",
    label: "Country",
    searchable: true,
    sortable: true,
    SearchComponent: ({ ...props }: any): any => (
      <CountrySelect
        {...props}
        label="Country"
        hookProps="country"
        onChange={(country: string | string[]): any =>
          props.onChange({
            country,
          })
        }
        defaultValue={[]}
        isSplitListBoxColumn
      />
    ),
  },
  subId: {
    label: "Subid",
    field: "subId",
    searchable: true,
    sortable: true,
    SearchComponent: ({ value, onChange }: any): any => (
      <StyledTextFieldMultiselectBody
        hookProps="subIdTransaction"
        value={value}
        label="Subid"
        placeholder="Input subId"
        name="subId"
        onChange={(subId: string | string[]): any =>
          onChange({
            subId,
          })
        }
      />
    ),
  },
  platform: {
    label: "Platform",
    field: "platform",
    sortable: true,
    searchable: true,
    SearchComponent: ({ ...props }: any): any => (
      <Select
        {...props}
        disableSearch
        isLoading={false}
        formControlSx={formControlSx}
        options={[
          {
            title: "Web",
            value: "web",
          },
          {
            title: "Mobile",
            value: "mobile",
          },
        ]}
        name="platform"
        data-testid="search-platform"
        placeholder="Select platform"
        onChange={(platform: string | string[]): any =>
          props.onChange({
            platform,
          })
        }
        defaultValue=""
      />
    ),
  },
  status: {
    label: "Status",
    field: "status",
    sortable: true,
    searchable: true,
    SearchComponent: ({ ...props }: any): any => (
      <Select
        {...props}
        disableSearch
        isLoading={false}
        formControlSx={formControlSx}
        options={[
          {
            title: "Approved",
            value: "Approved",
          },
          {
            title: "Declined",
            value: "Declined",
          },
        ]}
        name="status"
        placeholder="Select status"
        onChange={(status: string | string[]): any =>
          props.onChange({
            status,
          })
        }
        defaultValue=""
      />
    ),
  },
  decline_reason: {
    label: "Decline reason",
    field: "decline_reason",
    sortable: true,
  },
  clickId: {
    label: "Clickid",
    field: "clickId",
    searchable: true,
    SearchComponent: ({ value, onChange }: any): any => (
      <StyledTextFieldMultiselectBody
        hookProps="clickIdTransaction"
        value={value}
        label="Clickid"
        placeholder="Input clickid"
        name="clickId"
        onChange={(clickId: string | string[]): any =>
          onChange({
            clickId,
          })
        }
      />
    ),
  },
  subId2: {
    label: "Subid2",
    field: "subId2",
    searchable: true,
    SearchComponent: ({ value, onChange }: any): any => (
      <StyledTextFieldMultiselectBody
        hookProps="subId2Transaction"
        value={value}
        label="Subid2"
        placeholder="Input subid2"
        name="subId2"
        onChange={(subId2: string | string[]): any =>
          onChange({
            subId2,
          })
        }
      />
    ),
  },
  smartlink_name: {
    label: "Smartlink name",
    field: "smartlink_name",
    sortable: true,
  },
  ip: {
    label: "IP",
    field: "ip",
    sortable: true,
  },
  device_type: {
    label: "Device type",
    field: "device_type",
    sortable: true,
  },
  device_brand: {
    label: "Device brand",
    field: "device_brand",
    sortable: true,
  },
  device_os: {
    label: "Device OS",
    field: "device_os",
    sortable: true,
  },
  browser: {
    label: "Browser",
    field: "browser",
    sortable: true,
  },
  connection_type: {
    label: "Connection type",
    field: "connection_type",
    sortable: true,
  },
  utm_term: {
    label: "Utm term",
    field: "utm_term",
    sortable: true,
    searchable: true,
    render: ({ utm_term: utmTerm }: any): any =>
      utmTerm === null ? "" : utmTerm,
    SearchComponent: ({ value, onChange }: any): any => (
      <StyledTextFieldMultiselectBody
        hookProps="utm_termTransaction"
        value={value}
        label="Utm term"
        placeholder="Input utm term"
        name="utm_term"
        onChange={(utmTerm: string | string[]): any =>
          onChange({
            utm_term: utmTerm,
          })
        }
      />
    ),
  },
  pixel: {
    label: "Pixel",
    field: "pixel",
    sortable: true,
  },
};
